import type { Hotspot } from '../../api/types.js';
import type { HotspotsSnapshot } from '../../hotspots/types.js';
import { t } from '../../i18n/init.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface HotspotStripProps {
  hotspots: readonly Hotspot[];
  snapshot: HotspotsSnapshot;
  embed: boolean;
}

export interface HotspotStripCallbacks {
  onHotspotOpen: (id: string) => void;
}

const CHIP_BASE =
  'background:transparent;color:#d4d4d8;border:0;border-radius:9999px;padding:0.25rem 0.625rem;font-family:system-ui,sans-serif;font-size:0.6875rem;cursor:pointer;transition:background 100ms;white-space:nowrap;max-width:9rem;overflow:hidden;text-overflow:ellipsis';

function styleChip(btn: HTMLButtonElement, active: boolean): void {
  btn.style.cssText = `${CHIP_BASE};${
    active
      ? 'background:rgba(168,85,247,0.30);color:#e9d5ff;box-shadow:inset 0 0 0 1px rgba(168,85,247,0.40)'
      : ''
  }`;
}

export function mountHotspotStrip(
  root: HTMLElement,
  initial: HotspotStripProps,
  callbacks: HotspotStripCallbacks,
): ComponentInstance<HotspotStripProps> {
  if (initial.hotspots.length === 0) {
    return {
      update() {
        /* no-op */
      },
      cleanup() {
        /* no-op */
      },
    };
  }

  const wrap = document.createElement('nav');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('aria-label', t('viewer.hotspot_strip.nav_label'));
  wrap.style.cssText = initial.embed
    ? 'position:fixed;bottom:3.5rem;left:50%;transform:translateX(-50%);z-index:11;pointer-events:auto'
    : 'position:fixed;bottom:6rem;left:50%;transform:translateX(-50%);z-index:11;pointer-events:auto';

  const pill = document.createElement('div');
  pill.style.cssText =
    'background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.08);padding:0.25rem 0.5rem;border-radius:9999px;display:flex;gap:0.25rem;max-width:min(80vw,42rem);overflow-x:auto';
  wrap.appendChild(pill);

  // Declare the closure-shared snapshot BEFORE wiring per-button listeners
  // so mouseenter reads the live value rather than the stale `initial` capture.
  let currentSnapshot = initial.snapshot;

  const buttons: HTMLButtonElement[] = [];
  initial.hotspots.forEach((h, idx) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.classList.add('viewer-ui-tap-target');
    b.dataset['hotspotId'] = h.id;
    b.setAttribute(
      'aria-label',
      t('viewer.hotspot_strip.chip_label', { n: idx + 1, title: h.title }),
    );
    setText(b, t('viewer.hotspot_strip.chip_text', { n: idx + 1, title: h.title }));
    styleChip(b, false);
    b.addEventListener('mouseenter', () => {
      if (currentSnapshot.openIndex !== idx) {
        b.style.backgroundColor = 'rgba(255,255,255,0.10)';
      }
    });
    b.addEventListener('mouseleave', () => {
      const active = currentSnapshot.openIndex === idx;
      styleChip(b, active);
    });
    b.addEventListener('click', () => callbacks.onHotspotOpen(h.id));
    pill.appendChild(b);
    buttons.push(b);
  });

  root.appendChild(wrap);

  const apply = (props: HotspotStripProps): void => {
    currentSnapshot = props.snapshot;
    wrap.style.display = props.snapshot.visible ? 'block' : 'none';
    buttons.forEach((btn, idx) => {
      const active = props.snapshot.openIndex === idx;
      styleChip(btn, active);
      if (active) {
        btn.setAttribute('aria-current', 'true');
      } else {
        btn.removeAttribute('aria-current');
      }
    });
  };

  apply(initial);

  return {
    update(props: HotspotStripProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
